import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { Button, Placeholder, Spinner, Text } from '@/shared/ui/primitives';
import { useAuth } from '../model/AuthContext';
import styles from './RequireAuth.module.css';

interface RequireAuthProps {
  pendingFallback?: ReactNode;
  errorFallback?: (message: string) => ReactNode;
  children: ReactNode;
}

const defaultPending = (
  <div className={styles.status} aria-live="polite">
    <Spinner />
    <Text weight="regular">Signing you in...</Text>
  </div>
);

function AuthenticationError({
  message,
  retry,
}: {
  message: string;
  retry: () => Promise<string>;
}) {
  const [isRetrying, setIsRetrying] = useState(false);
  const handleRetry = async (): Promise<void> => {
    if (isRetrying) return;
    setIsRetrying(true);
    try {
      await retry();
    } catch {
      // The auth provider owns the error and keeps the retry action available.
    } finally {
      setIsRetrying(false);
    }
  };
  return (
    <Placeholder
      className={styles.error}
      role="alert"
      title="Could not sign you in"
      description={message}
      action={
        <Button loading={isRetrying} onClick={() => void handleRetry()}>
          Try again
        </Button>
      }
    />
  );
}

export const RequireAuth: FC<RequireAuthProps> = ({
  pendingFallback = defaultPending,
  errorFallback,
  children,
}) => {
  const { isAuthenticated, authError, authenticate } = useAuth();

  if (authError) {
    return errorFallback ? (
      <>{errorFallback(authError)}</>
    ) : (
      <AuthenticationError message={authError} retry={authenticate} />
    );
  }
  if (!isAuthenticated) return <>{pendingFallback}</>;
  return <>{children}</>;
};
